import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'

import { always, compose, concat, mergeDeepWith, sortBy, prop } from 'ramda'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectCoursesGroupedBySection,
  selectSectionsSorted,
  selectCourses,
  selectSeries,
  selectCoursesStatus,
  selectSeriesStatus,
  selectIncompleteAssignmentCourses,
  selectHeroSlides,
  selectHeroSlidesStatus,
  selectActionableCourseProgress,
  selectCourseProgressHasLoadedOnce,
  selectCourseProgressByCourseIDMap,
  fetchHeroSlides,
} from 'src/v3/self-paced/self-paced.slice'
import { ActionItemCard } from 'src/v3/self-paced/components/action-item-card'
import { ActionItemsCarouselSkeleton } from 'src/v3/self-paced/components/skeletons'
import { CourseProgressSummary } from 'src/v3/self-paced/types'
import {
  selectUseTexasJurisprudence,
  TEXAS_JURISPRUDENCE_COURSE_IDS,
} from 'src/v3/settings/settings.slice'
import {
  ReactElement,
  useState,
  useEffect,
  useCallback,
  memo,
  useMemo,
} from 'react'
import useSession from 'src/authentication/hooks/use-session'
import { useAutoTour } from 'src/v3/tours/hooks/use-auto-tour'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import { SIDEBAR_NAVIGATION_TOUR_ID } from 'src/v3/tours/definitions/sidebar-navigation.tour'
import withConfirm from 'src/components/common/with-confirm'
import { HeroCarousel } from './components/hero-carousel'
import { HorizontalCarousel } from './components/horizontal-carousel'
import { QuickAccessBanner } from './components/quick-access-banner'
import {
  HeroCarouselSkeleton,
  HorizontalCarouselSkeleton,
} from './components/skeletons'

// Progressive Loading Wrapper Component
let ProgressiveLoader = memo(
  ({
    status,
    data,
    skeleton,
    children,
    dataValidator,
  }: {
    status: string
    data: any
    skeleton: React.ReactNode
    children: React.ReactNode
    dataValidator?: (d: any) => boolean
  }) => {
    let isDataValid = dataValidator
      ? dataValidator(data)
      : data && Array.isArray(data)
        ? data.length > 0
        : !!data

    if (status !== 'fulfilled' || !isDataValid) {
      return <>{skeleton}</>
    }
    return <>{children}</>
  },
)

// Section Loader Component
let SectionLoader = memo(
  ({
    sections,
    coursesStatus,
    courses,
    incompleteAssignmentCourses,
    handleCourseClick,
    isLocked,
    navigate,
    boundTreeStatus,
    courseProgress,
  }: {
    sections: any[]
    coursesStatus: string
    courses: any
    incompleteAssignmentCourses: any[]
    handleCourseClick: (id: string) => void
    isLocked: boolean
    navigate: any
    boundTreeStatus?: string
    courseProgress?: Record<string, CourseProgressSummary>
  }) => {
    let skeletonElements = useMemo(
      () => (
        <>
          {sections.map((section) => (
            <HorizontalCarouselSkeleton key={section.id} title={section.name} />
          ))}
        </>
      ),
      [sections],
    )

    let contentElements = useMemo(
      () => (
        <>
          {sections.map((section) => (
            <HorizontalCarousel
              key={section.id}
              title={section.name}
              courses={mergeDeepWith(concat, [], courses)?.[section.id] || []}
              onCourseClick={handleCourseClick}
              assignedCourses={incompleteAssignmentCourses || []}
              isLocked={isLocked}
              sectionId={section.id}
              onNavigate={navigate}
              boundTreeStatus={boundTreeStatus}
              courseProgress={courseProgress}
            />
          ))}
        </>
      ),
      [
        sections,
        courses,
        incompleteAssignmentCourses,
        handleCourseClick,
        isLocked,
        navigate,
        courseProgress,
      ],
    )

    return (
      <ProgressiveLoader
        status={coursesStatus}
        data={courses}
        dataValidator={() => true} // Sections can be empty
        skeleton={skeletonElements}
      >
        {contentElements}
      </ProgressiveLoader>
    )
  },
)

let boundtreeSectionID = '9310c4d1-7447-47d6-a01a-60f1b87d68ac'

let boundTreeFreeCourseIDs = [
  'b4995e14-8480-4b5d-99a7-aa814b0c45d1',
  '039c7217-4172-474f-b4c1-f47bcab67310',
  '0646715f-54ac-45d0-a649-a72b6cacda60',
  'b85cd107-e5f1-4ecb-a0dd-e9e3fda4ad0d',
  '3bd2fcc3-80f7-4486-9b72-8b2c7f652148',

  'ca1ddd61-6a0c-43ff-902d-21ab2a791f26',
  '348d5270-dfdb-408d-a255-4e91ba79d99d',
  '0c458a7b-7def-47f6-998d-7179a350fdc9',
  'dce6541e-2d57-45a8-a888-6cc41fb07ea4',
  '49391a29-840b-4c31-b51e-965dd27279bd',
]

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let session = useSession()

  let sections = useAppSelector(selectSectionsSorted)
  let courses = useAppSelector(selectCoursesGroupedBySection)
  let allCourses = useAppSelector(selectCourses)
  let series = useAppSelector(selectSeries)
  let incompleteAssignmentCourses = useAppSelector(
    selectIncompleteAssignmentCourses,
  )
  let heroSlides = useAppSelector(selectHeroSlides)
  let heroSlidesStatus = useAppSelector(selectHeroSlidesStatus)
  let actionableProgress = useAppSelector(selectActionableCourseProgress)
  let courseProgressHasLoadedOnce = useAppSelector(
    selectCourseProgressHasLoadedOnce,
  )
  let courseProgressByCourseID = useAppSelector(
    selectCourseProgressByCourseIDMap,
  )

  let coursesStatus = useAppSelector(selectCoursesStatus)
  let seriesStatus = useAppSelector(selectSeriesStatus)
  let useTexasJurisprudence = useAppSelector(selectUseTexasJurisprudence)
  // Progress can be ready before the separate courses fetch resolves — each
  // ActionItemCard looks up its course and renders nothing without one, so
  // without this the slider would render empty instead of staying on
  // skeleton.
  let actionItemsLoaded =
    courseProgressHasLoadedOnce && coursesStatus === 'fulfilled'

  useAutoTour()
  usePageTour(SIDEBAR_NAVIGATION_TOUR_ID)

  let [confirmStatus, setConfirmStatus] = useState<'Closed' | 'Open'>('Closed')

  // Fetch hero slides if not loaded
  useEffect(() => {
    if (heroSlidesStatus === 'uninitialized') {
      dispatch(fetchHeroSlides())
    }
  }, [dispatch, heroSlidesStatus])

  let ConfirmUpgrade = withConfirm(
    'Upgrade to Full Access',
    'IMPORTANT: You once you pay for full access, you will need to log out of Studio and back in to access all of the content.',
    confirmStatus === 'Open',
    compose(setConfirmStatus, always('Closed') as any),
    () => {
      Promise.resolve().then(() =>
        navigate(
          `/subscribe-boundtree?email=${session?.emailAddress
            .toLowerCase()
            .trim()}`,
        ),
      )
    },
  )

  let texasCourses = allCourses.filter(
    (c) =>
      TEXAS_JURISPRUDENCE_COURSE_IDS.includes(c.id) &&
      c.published === 'published',
  )

  let filteredCourses = Object.fromEntries(
    Object.entries(courses).map(([sectionId, sectionCourses]) => [
      sectionId,
      (sectionCourses as any[]).filter(
        (c) => !TEXAS_JURISPRUDENCE_COURSE_IDS.includes(c.id),
      ),
    ]),
  )

  let boundtreeCourses = allCourses.filter((course) =>
    boundTreeFreeCourseIDs.includes(course.id),
  )

  // Get hero series

  let handleCourseClick = useCallback(
    (courseId: string) => {
      navigate(`/self-paced/course/${courseId}`)
    },
    [navigate],
  )

  // Get sections excluding BoundTree and sections with no published courses
  let filteredSections =
    sections?.filter(
      (x) =>
        x.id !== boundtreeSectionID && (filteredCourses[x.id] || []).length > 0,
    ) || []
  let firstSection = filteredSections.slice(0, 1)
  let nextTwoSections = filteredSections.slice(1, 3)
  let remainingSections = filteredSections.slice(3)

  return (
    <Box
      sx={{
        bgcolor: '#000000',
        minHeight: '100vh',
        color: 'white',
        pb: 4,
      }}
    >
      <Box>
        {/* Hero Carousel Section - Only for non-limited users */}
        {session?.boundTreeStatus !== 'limited' && (
          <ProgressiveLoader
            status={heroSlidesStatus}
            data={heroSlides}
            skeleton={<HeroCarouselSkeleton />}
          >
            <HeroCarousel
              slides={sortBy(prop('sequence'), heroSlides)}
              onNavigate={navigate}
            />
          </ProgressiveLoader>
        )}

        {/* Free Courses for Limited Users - Moved to top */}
        {session?.boundTreeStatus === 'limited' && (
          <ProgressiveLoader
            status={coursesStatus}
            data={boundtreeCourses}
            skeleton={<HorizontalCarouselSkeleton title="Free Courses" />}
          >
            <HorizontalCarousel
              title="Free Courses"
              courses={boundtreeCourses}
              onCourseClick={handleCourseClick}
              assignedCourses={[]}
              isLocked={false}
              boundTreeStatus={session?.boundTreeStatus}
              courseProgress={courseProgressByCourseID}
            />
          </ProgressiveLoader>
        )}

        {/* Quick Access Banners for Limited Users - Right after Free Courses */}
        {session?.boundTreeStatus === 'limited' && (
          <Box sx={{ my: 6, px: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                },
                gap: 3,
              }}
            >
              <QuickAccessBanner
                onClick={compose(setConfirmStatus, always('Open') as any)}
                backgroundImage={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/BoundTreeFullAccess.png`}
              />
              <QuickAccessBanner
                onClick={() =>
                  window.open('https://www.boundtree.com/education', '_blank')
                }
                backgroundImage={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/BoundTreeService.png`}
              />
            </Box>
          </Box>
        )}

        {/* Continue Watching */}
        {!actionItemsLoaded ? (
          <ActionItemsCarouselSkeleton
            title="Continue Watching"
            subtitle="Pick up where you left off"
          />
        ) : (
          actionableProgress.length > 0 && (
            <HorizontalCarousel
              title="Continue Watching"
              subtitle="Pick up where you left off"
              courses={actionableProgress}
              onCourseClick={() => {}}
              renderItem={(p: CourseProgressSummary) => (
                <ActionItemCard
                  key={p.courseID}
                  progress={p}
                  course={allCourses.find((c) => c.id === p.courseID)}
                  onNavigate={navigate}
                />
              )}
            />
          )
        )}

        {/* First Section */}
        <SectionLoader
          sections={firstSection}
          coursesStatus={coursesStatus}
          courses={filteredCourses}
          incompleteAssignmentCourses={incompleteAssignmentCourses}
          handleCourseClick={handleCourseClick}
          isLocked={session?.boundTreeStatus === 'limited'}
          navigate={navigate}
          boundTreeStatus={session?.boundTreeStatus}
          courseProgress={courseProgressByCourseID}
        />

        {/* Series Section */}
        <ProgressiveLoader
          status={seriesStatus}
          data={series}
          skeleton={<HorizontalCarouselSkeleton title="Series" isSeries />}
        >
          <HorizontalCarousel
            title="Series"
            courses={series.map((s) => ({
              ...s,
              displayName: s.name,
              imageName: s.mobileBanner?.replace('originals', 'resized'),
              isSeries: true,
            }))}
            onCourseClick={(id: string) => navigate(`/self-paced/series/${id}`)}
            assignedCourses={[]}
            isLocked={session?.boundTreeStatus === 'limited'}
            onNavigate={navigate}
            boundTreeStatus={session?.boundTreeStatus}
          />
        </ProgressiveLoader>

        {/* Next Two Sections */}
        <SectionLoader
          sections={nextTwoSections}
          coursesStatus={coursesStatus}
          courses={filteredCourses}
          incompleteAssignmentCourses={incompleteAssignmentCourses}
          handleCourseClick={handleCourseClick}
          isLocked={session?.boundTreeStatus === 'limited'}
          navigate={navigate}
          boundTreeStatus={session?.boundTreeStatus}
          courseProgress={courseProgressByCourseID}
        />

        {/* Quick Access Banners for Non-Limited Users */}
        {session?.boundTreeStatus !== 'limited' && (
          <Box sx={{ my: 6, px: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              <QuickAccessBanner
                title="Live Classes"
                description="Join our live interactive sessions"
                onClick={() => navigate('/live')}
                backgroundImage={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/live-banner.png`}
              />
              <QuickAccessBanner
                title="Learning Paths"
                description="Explore learning paths"
                onClick={() => navigate('/profile/learning-paths')}
                backgroundImage={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/learning-paths-banner.png`}
              />
              <QuickAccessBanner
                title="IBSC 100"
                description="Track progress in the IBSC 100 hour refresher"
                onClick={() => navigate('/profile/ibsc-100')}
                backgroundImage={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/ibsc-banner.png`}
              />
            </Box>
          </Box>
        )}

        {/* Remaining Sections */}
        <SectionLoader
          sections={remainingSections}
          coursesStatus={coursesStatus}
          courses={filteredCourses}
          incompleteAssignmentCourses={incompleteAssignmentCourses}
          handleCourseClick={handleCourseClick}
          isLocked={session?.boundTreeStatus === 'limited'}
          navigate={navigate}
          boundTreeStatus={session?.boundTreeStatus}
          courseProgress={courseProgressByCourseID}
        />

        {/* Texas Jurisprudence Section */}
        {useTexasJurisprudence && texasCourses.length > 0 && (
          <ProgressiveLoader
            status={coursesStatus}
            data={texasCourses}
            skeleton={
              <HorizontalCarouselSkeleton title="Texas Jurisprudence" />
            }
          >
            <HorizontalCarousel
              title="Texas Jurisprudence"
              courses={texasCourses}
              onCourseClick={handleCourseClick}
              assignedCourses={incompleteAssignmentCourses || []}
              isLocked={session?.boundTreeStatus === 'limited'}
              boundTreeStatus={session?.boundTreeStatus}
              courseProgress={courseProgressByCourseID}
            />
          </ProgressiveLoader>
        )}

        <ConfirmUpgrade />
      </Box>
    </Box>
  )
}
